'use client';

import { Loader } from '@brahma/components';
import React, { createContext, useContext, useState, useEffect } from 'react';
export type Theme = 'primary' | 'green' | 'brown' | 'lmdc-green' | 'lbnm-blue' | 'nmcb-green' | 'mcm-blue';
type ThemeContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
export const ThemeProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [theme, setTheme] = useState<Theme>('primary'); // Default theme
  const [isLoaded, setIsLoaded] = useState(false); // Track when localStorage has been read

  useEffect(() => {
    // Only runs on the client side
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const savedTheme = localStorage.getItem('theme') as Theme;
      if (savedTheme) {
        setTheme(savedTheme);
      }
    }
    setIsLoaded(true); // Mark as loaded
  }, []);
  useEffect(() => {
    if (isLoaded && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      // Apply the theme to the HTML element
      const rootElement = document.documentElement;
      rootElement.setAttribute('data-theme', theme);

      // Save the theme to localStorage
      localStorage.setItem('theme', theme);
    }
  }, [theme, isLoaded]);
  if (!isLoaded) {
    //  fallback UI while waiting for the theme to load
    return <Loader show={true} />;
  }
  return <ThemeContext.Provider value={{
    theme,
    setTheme
  }} data-sentry-element="unknown" data-sentry-component="ThemeProvider" data-sentry-source-file="index.tsx">
      {children}
    </ThemeContext.Provider>;
};
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};